/**
 * useFinancialAnalysis - Hook for calculating comprehensive financial analysis
 *
 * Encapsulates the complex logic for calculating NPV, payback period, cashflows,
 * and other financial metrics from optimization results and budget data.
 * Extracted from ScenarioEditor to improve maintainability.
 *
 * @module hooks/useFinancialAnalysis
 */

import { useMemo } from 'react';
import { FinancialCalculator } from '../FinancialAnalysis';
import { debug } from '../utils/debug';

/**
 * Hook for calculating comprehensive financial analysis
 *
 * @param {Object} options
 * @param {Object} options.optimization - Optimization state with results
 * @param {Object} options.monthlyPerformanceData - Monthly performance data with billing info
 * @param {boolean} options.isLoadingScenario - Whether scenario is currently loading
 * @param {string} options.scenarioId - Current scenario ID ('new' for new scenarios)
 * @param {Object} options.budget - Budget state with capex, opex, and itc calculations
 * @param {Object} options.commercialAssumptions - Commercial assumptions with escalation rates
 * @param {Object} options.gasTariffData - Gas tariff data for tiered cost calculation
 * @returns {Object|null} Comprehensive financial analysis or null if not ready
 */
export function useFinancialAnalysis({
    optimization,
    monthlyPerformanceData,
    isLoadingScenario,
    scenarioId,
    budget,
    commercialAssumptions,
    gasTariffData,
}) {
    const financialAnalysis = useMemo(() => {
        if (!optimization?.results) {
            debug.log('📊 [useFinancialAnalysis] No optimization results yet');
            return null;
        }

        // CRITICAL: Wait for scenario to finish loading before calculating
        // This ensures budget state is restored from backend before we calculate NPV
        // Without this, we calculate with default CapEx values before saved values load
        if (isLoadingScenario && scenarioId !== 'new') {
            debug.log('📊 [useFinancialAnalysis] Waiting for scenario to load (budget state not yet restored)...');
            return null;
        }

        // Wait for monthly performance data before calculating
        if (!monthlyPerformanceData) {
            debug.log('📊 [useFinancialAnalysis] Waiting for monthly performance data...');
            return null;
        }

        // CRITICAL: Ensure monthly data matches current optimization run
        // Stale data from previous runs will show wrong NPV until fresh data loads
        const currentRunUuid = optimization.results?.run_uuid;
        if (monthlyPerformanceData._runUuid && currentRunUuid &&
            monthlyPerformanceData._runUuid !== currentRunUuid) {
            debug.log('📊 [useFinancialAnalysis] Monthly data is stale (different run UUID), waiting for fresh data...');
            return null;
        }

        // CRITICAL: Wait for budget to finish loading before calculating
        // This prevents showing stale/default values before scenario budget is restored
        if (budget?.loading) {
            debug.log('📊 [useFinancialAnalysis] Waiting for budget to load...');
            return null;
        }

        // CRITICAL: Budget tab is the ONLY source of capital costs
        // We should NEVER use solver's financial costs
        const overrideCapitalCost = budget?.capexCalculations?.total;
        const overrideItcAmount = budget?.itcCalculations?.totalItc || 0;

        if (overrideCapitalCost == null) {
            debug.error('❌ [useFinancialAnalysis] Budget CapEx calculations not available');
            return null;
        }

        debug.log('📊 [useFinancialAnalysis] Calculating financial analysis (Budget CapEx Only):', {
            hasResults: !!optimization?.results,
            hasMonthlyData: !!monthlyPerformanceData,
            capitalCost: overrideCapitalCost,
            itcAmount: overrideItcAmount,
            netAfterItc: budget?.itcCalculations?.netCostAfterItc,
            hasOpExData: !!budget?.opexCalculations,
            // DETAILED BUDGET DATA FOR CASHFLOW DEBUG
            opexBreakdown: budget?.opexCalculations ? {
                pvOM: budget.opexCalculations.pvOM,
                batteryOM: budget.opexCalculations.batteryOM,
                generatorOM: budget.opexCalculations.generatorOM,
                fixedAnnualOM: budget.opexCalculations.fixedAnnualOM,
                totalAnnualOM: budget.opexCalculations.totalAnnualOM
            } : 'NOT AVAILABLE',
            capexBreakdown: budget?.capexCalculations ? {
                equipment: budget.capexCalculations.equipment,
                installation: budget.capexCalculations.installation,
                softCostsTotal: budget.capexCalculations.softCostsTotal,
                total: budget.capexCalculations.total
            } : 'NOT AVAILABLE',
            itcBreakdown: budget?.itcCalculations ? {
                totalItc: budget.itcCalculations.totalItc,
                netCostAfterItc: budget.itcCalculations.netCostAfterItc
            } : 'NOT AVAILABLE'
        });

        const calculator = new FinancialCalculator(
            optimization.results,
            {},
            monthlyPerformanceData,
            overrideCapitalCost,
            budget?.opexCalculations, // Pass custom OpEx calculations
            overrideItcAmount, // Pass ITC amount for proper net cost calculation
            commercialAssumptions?.calculatorInputs?.escalationRates, // Pass escalation rates from Commercial tab
            gasTariffData, // Pass gas tariff for tiered cost calculation
            commercialAssumptions?.calculatorInputs // Pass full commercial assumptions
        );
        const analysis = calculator.getComprehensiveAnalysis();

        debug.log('📊 [useFinancialAnalysis] Financial analysis result:', {
            npv: analysis?.npv,
            paybackPeriod: analysis?.paybackPeriod,
            hasCashflows: !!analysis?.cashflows,
            cashflowsLength: analysis?.cashflows?.length,
            annualOM: analysis?.systemCosts?.annualOmCosts
        });

        return analysis;
    }, [
        optimization?.results,
        monthlyPerformanceData,
        isLoadingScenario,
        scenarioId,
        budget?.loading,
        budget?.capexCalculations?.total,
        budget?.itcCalculations?.totalItc,
        budget?.opexCalculations,
        commercialAssumptions?.calculatorInputs,
        gasTariffData
    ]);

    return financialAnalysis;
}

export default useFinancialAnalysis;
